/**
 * RescheduleConsultDialog — accessible dialog to move a booked consultation to
 * a new slot. REUSES Door A's slot engine + view helpers (`computeOpenSlots`,
 * `describeSlot`, `groupSlotsByDay`) and the seeded availability, so the picker
 * matches the staff booking panel. Confirm step makes clear the client will be
 * notified automatically (email + WhatsApp), then calls the team-auth
 * `rescheduleConsultation` seam. Handles `slot_taken` (re-pick) and errors
 * honestly; the dialog only closes on a real success.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import AdminDialogShell from './AdminDialogShell';
import type { AdminConsultation } from '../lib/consultAdminStore';
import type { ConsultAdminApi } from '../lib/consultAdminApi';
import {
  DEFAULT_AVAILABILITY,
  type AvailabilitySettings,
} from '../lib/types';
import {
  listenAvailabilitySettings,
  fetchFreeBusy,
  fetchBookedSlotStarts,
} from '../lib/consultStore';
import { computeOpenSlots, type FreeBusyInterval } from '../lib/availability';
import { describeSlot, dropBookedSlots } from '../lib/slotView';

const HORIZON_DAYS = 14;

export default function RescheduleConsultDialog({
  consult,
  api,
  whenLabel,
  onClose,
  onRescheduled,
  announce,
}: {
  consult: AdminConsultation;
  api: ConsultAdminApi;
  whenLabel: string;
  onClose: () => void;
  onRescheduled: (newStartISO: string, newEndISO: string) => void;
  announce: (msg: string) => void;
}) {
  const [now] = useState(() => new Date().toISOString());
  const [avail, setAvail] = useState<AvailabilitySettings>(DEFAULT_AVAILABILITY);
  const [busyIntervals, setBusyIntervals] = useState<FreeBusyInterval[]>([]);
  const [bookedStarts, setBookedStarts] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<{ startISO: string; endISO: string } | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  useEffect(() => listenAvailabilitySettings(setAvail), []);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const endISO = new Date(Date.parse(now) + HORIZON_DAYS * 86_400_000).toISOString();
    void Promise.all([fetchFreeBusy(now, endISO), fetchBookedSlotStarts(now, endISO)]).then(
      ([fb, starts]) => {
        if (!alive) return;
        setBusyIntervals(fb.busy);
        setBookedStarts(starts);
        setLoading(false);
      },
    );
    return () => {
      alive = false;
    };
  }, [now]);

  const slots = useMemo(() => {
    const raw = computeOpenSlots(busyIntervals, avail.weekly, null, {
      durationMin: avail.defaultSlotMin,
      bufferMin: avail.defaultBufferMin,
      minNoticeHours: avail.minNoticeHours,
      maxPerDay: avail.maxPerDay,
      horizonDays: HORIZON_DAYS,
      nowISO: now,
      timeZone: avail.timeZone,
      blockedDates: avail.hardBlocks.map((b) => b.date),
    });
    // Never offer the slot this booking already occupies, and drop taken ones.
    const withoutCurrent = raw.filter((s) => s.startISO !== consult.startISO);
    return dropBookedSlots(withoutCurrent, bookedStarts);
  }, [busyIntervals, avail, now, bookedStarts, consult.startISO]);

  // Proper radiogroup keyboard nav (arrows / Home / End) with roving tabindex.
  const slotsRef = useRef<HTMLDivElement>(null);
  function onSlotsKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    const keys = ['ArrowDown', 'ArrowRight', 'ArrowUp', 'ArrowLeft', 'Home', 'End'];
    if (!keys.includes(e.key) || slots.length === 0) return;
    e.preventDefault();
    const n = slots.length;
    const cur = selected ? slots.findIndex((s) => s.startISO === selected.startISO) : -1;
    let next: number;
    if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = n - 1;
    else {
      const dir = e.key === 'ArrowDown' || e.key === 'ArrowRight' ? 1 : -1;
      const base = cur < 0 ? (dir > 0 ? -1 : 0) : cur;
      next = (base + dir + n) % n;
    }
    const s = slots[next];
    setSelected({ startISO: s.startISO, endISO: s.endISO });
    setNotice('');
    slotsRef.current
      ?.querySelectorAll<HTMLButtonElement>('[role="radio"]')
      ?.[next]?.focus();
  }

  async function confirmReschedule() {
    if (!selected || submitting) return;
    setSubmitting(true);
    setError('');
    setNotice('');
    announce('Moving the call…');
    const res = await api.reschedule(consult.id, selected.startISO);
    if (res.kind === 'ok') {
      announce('The call was moved and the client notified.');
      onRescheduled(selected.startISO, selected.endISO);
      return;
    }
    setSubmitting(false);
    if (res.kind === 'slot_taken') {
      setSelected(null);
      setBookedStarts((prev) => [...prev, selected.startISO]);
      setNotice('That time was just taken — please pick another.');
      announce('That time was just taken. Please pick another.');
    } else {
      setError(res.message);
      announce(res.message);
    }
  }

  return (
    <AdminDialogShell
      labelledById="cad-res-title"
      describedById="cad-res-body"
      busy={submitting}
      onClose={onClose}
    >
      <h2 className="cad-title" id="cad-res-title">
        Reschedule {consult.clientName}
      </h2>
      <p className="cad-sub" id="cad-res-body">
        Pick a new time. We&apos;ll move the calendar event and keep the same Meet
        link.
      </p>

      <div className="cad-current">
        <div>
          <b>Current time</b>
          <br />
          {whenLabel}
        </div>
      </div>

      <p className="cad-sect">Choose a new time (next {HORIZON_DAYS} days · IST)</p>

      {loading ? (
        <div className="cad-empty" role="status">
          <span className="cad-spinner" aria-hidden="true" /> Loading open times…
        </div>
      ) : slots.length === 0 ? (
        <div className="cad-empty" role="status">
          No open times in the next {HORIZON_DAYS} days. Widen availability in
          Settings, or cancel instead.
        </div>
      ) : (
        <div
          className="cad-slots"
          role="radiogroup"
          aria-label="Open times — use arrow keys to choose"
          ref={slotsRef}
          onKeyDown={onSlotsKeyDown}
        >
          {slots.map((slot, i) => {
            const d = describeSlot(slot, avail.timeZone);
            const isSel = selected?.startISO === slot.startISO;
            // Roving tabindex: the checked radio is tabbable; if none is checked
            // yet, the first radio is the group's single tab stop.
            const tabbable = isSel || (!selected && i === 0);
            return (
              <button
                key={slot.startISO}
                type="button"
                role="radio"
                aria-checked={isSel}
                tabIndex={tabbable ? 0 : -1}
                className="cad-slot"
                onClick={() => {
                  setSelected({ startISO: slot.startISO, endISO: slot.endISO });
                  setNotice('');
                }}
              >
                <span>
                  <span className="cad-slot-when">
                    {d.weekday}, {d.dayNum} {d.monthShort} · {d.time}
                  </span>
                  <span className="cad-slot-sub">
                    {avail.defaultSlotMin} min · IST
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      )}

      {notice && (
        <p className="cad-err" role="status" style={{ background: 'var(--warn-wash)', color: 'var(--warn-ink)', borderColor: 'var(--warn-edge)' }}>
          {notice}
        </p>
      )}

      <div className="cad-notify">
        <div>
          <b>The client is notified automatically</b> — a new confirmation (email +
          WhatsApp) with the updated time goes out the moment you confirm.
        </div>
      </div>

      {error && (
        <p className="cad-err" role="alert">
          {error}
        </p>
      )}

      <div className="cad-actions">
        <button type="button" className="ca-btn ca-btn-line" onClick={onClose} disabled={submitting}>
          Keep current time
        </button>
        <button
          type="button"
          className="ca-btn ca-btn-primary"
          onClick={() => void confirmReschedule()}
          disabled={!selected || submitting}
        >
          {submitting ? (
            <>
              <span className="cad-spinner" aria-hidden="true" /> Moving…
            </>
          ) : (
            'Confirm new time & notify'
          )}
        </button>
      </div>
    </AdminDialogShell>
  );
}
